import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/react'
import { LockClosedIcon } from '@heroicons/react/16/solid'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { AnimatePresence, motion } from 'framer-motion'
import initials from 'initials'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useTalentStorage } from '../../features/pricing/hooks'
import { classNames } from '../../helpers'
import {
  isStorageLocked,
  isStoragePending
} from '../../helpers/talent-storage-table'
import { useTalentsQuery } from '../../hooks/use-talents-query'

const STATUS_COLOR_MAP = {
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-600'
}

const TalentSearchCombobox = ({ currentSpaceId }) => {
  const [query, setQuery] = useState('')
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { status, data } = useTalentsQuery()
  const spaces = useMemo(() => data?.spaces || [], [data])

  // Gesperrte Talente (Talentspeicher, nur in enforced) bekommen ein Schloss,
  // öffnen lässt sich die Detailseite trotzdem. Der Speicher liest nur den
  // Cache dieser Query. Vorläufig maskierte Zeilen (storage_pending) und ein
  // noch nicht gezählter Speicher zeigen kein Schloss, die Detailseite wartet
  // selbst.
  const storage = useTalentStorage()
  const isLockedEntry = (space) =>
    !storage.pending &&
    !isStoragePending(space) &&
    (isStorageLocked(space) || storage.lockedIds.has(space.id))

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim()
    const candidates = spaces.filter((s) => s.id !== currentSpaceId)

    if (!q) return candidates.slice(0, 7)

    return candidates
      .filter(
        (s) =>
          s.identification?.toLowerCase().includes(q) ||
          s.email?.toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [query, spaces, currentSpaceId])

  const handleSelect = (space) => {
    if (!space) return
    setQuery('')
    navigate(`/talent/${space.id}`)
  }

  return (
    <Combobox
      value={null}
      onChange={handleSelect}
      immediate
      onClose={() => setQuery('')}
    >
      {({ open }) => (
        <div className='relative rounded-md shadow-sm'>
          <div
            className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'
            aria-hidden='true'
          >
            <MagnifyingGlassIcon className='h-4 w-4 text-gray-400' />
          </div>
          <ComboboxInput
            className={classNames(
              'block w-52 rounded-md border-0 py-1.5 pl-9 pr-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 sm:text-sm sm:leading-6',
              open
                ? 'ring-2 ring-blue-600'
                : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600'
            )}
            placeholder={t(
              'pages.talents.search.placeholder',
              'Talent suchen ...'
            )}
            onChange={(e) => setQuery(e.target.value)}
            displayValue={() => query}
          />
          <AnimatePresence>
            {open && (
              <ComboboxOptions
                as={motion.div}
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.1 }}
                static
                className='absolute right-0 z-50 mt-1.5 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl focus:outline-none'
              >
                {status === 'pending' && (
                  <div className='px-4 py-3 text-sm text-gray-500'>
                    {t(
                      'pages.talents.search.no_cache',
                      'Talentliste wird geladen...'
                    )}
                  </div>
                )}
                {spaces.length > 0 && filtered.length === 0 && query && (
                  <div className='px-4 py-3 text-sm text-gray-500'>
                    {t(
                      'pages.talents.search.no_results',
                      'Kein Talent gefunden'
                    )}
                  </div>
                )}
                {filtered.map((space) => {
                  const statusColor = space.assessment_status?.status?.color
                  const colorClass =
                    STATUS_COLOR_MAP[statusColor] || STATUS_COLOR_MAP.gray
                  const statusLabel = space.assessment_status?.status?.label
                    ? t(space.assessment_status.status.label, {
                        progress: space.assessment_status.status.progress
                      })
                    : null
                  const locked = isLockedEntry(space)

                  return (
                    <ComboboxOption
                      key={space.id}
                      value={space}
                      className='cursor-pointer select-none'
                    >
                      {({ focus }) => (
                        <div
                          className={classNames(
                            'flex items-center gap-x-3 px-3 py-2.5 transition-colors',
                            focus ? 'bg-blue-50' : 'bg-white'
                          )}
                        >
                          <span className='inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-700'>
                            {initials(space.initials) || '?'}
                          </span>
                          <div className='min-w-0 flex-1'>
                            {locked ? (
                              <p className='flex items-center gap-x-1 text-sm font-medium text-gray-500'>
                                <LockClosedIcon
                                  aria-hidden='true'
                                  className='size-3.5 shrink-0 text-gray-400'
                                />
                                <span className='truncate'>
                                  {space.identification}
                                </span>
                                <span className='sr-only'>
                                  {t('pricing.talent_locked.row_sr')}
                                </span>
                              </p>
                            ) : (
                              <p className='truncate text-sm font-medium text-gray-900'>
                                {space.identification}
                              </p>
                            )}
                            {space.email && space.name && (
                              <p className='truncate text-xs text-gray-500'>
                                {space.email}
                              </p>
                            )}
                          </div>
                          {statusLabel && (
                            <span
                              className={classNames(
                                'flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                                colorClass
                              )}
                            >
                              {statusLabel}
                            </span>
                          )}
                        </div>
                      )}
                    </ComboboxOption>
                  )
                })}
                <div className='border-t border-gray-100 px-3 py-1.5'>
                  <p className='text-xs text-gray-400'>
                    ↑↓ {t('pages.talents.search.hint_navigate', 'navigieren')}{' '}
                    &middot; ↵ {t('pages.talents.search.hint_open', 'öffnen')}{' '}
                    &middot; esc{' '}
                    {t('pages.talents.search.hint_close', 'schließen')}
                  </p>
                </div>
              </ComboboxOptions>
            )}
          </AnimatePresence>
        </div>
      )}
    </Combobox>
  )
}

TalentSearchCombobox.propTypes = {
  currentSpaceId: PropTypes.string
}

TalentSearchCombobox.defaultProps = {
  currentSpaceId: undefined
}

export default TalentSearchCombobox
